import { useEffect, useMemo, useState } from 'react';
import { BiPlus, BiSearch } from 'react-icons/bi';
import { FaClockRotateLeft } from 'react-icons/fa6';
import { useSearchParams } from 'react-router-dom';

import { ButtonOutlinedSmall, ButtonSmall } from '@/components/Button';
import { DateNavPicker } from '@/components/DateNavPicker';
import { Input } from '@/components/Inputs';
import { Tile } from '@/components/Tile/Tile';
import { useIsMobile } from '@/hooks/useIsMobile';
import { PERMISSIONS } from '@/permissions/permissions.catalog';
import { usePermissions } from '@/permissions/usePermissions';
import {
  useCreateTrashLogMutation,
  useSignTrashLogMutation,
  useTrashLogPdfMutation,
  useTrashLogsByDateQuery,
  useUpdateTrashLogMutation,
} from '@/store/trash-log/trash-log.store';
import { useVessels } from '@/store/vessels/vessels.store';
import { downloadBlob } from '@/utils/download-file-from-link';
import { getBrowserTimeZone } from '@/utils/timezone';
import { toast } from '@/utils/toast';

import { LOG_VIEW_PARAM, useLogView } from '../useLogView';

import { TrashCategoriesReference } from './components/TrashCategoriesReference';
import { TrashEntriesSection } from './components/TrashEntriesSection';
import { TrashFooterSection } from './components/TrashFooterSection';
import { TrashHeaderFields } from './components/TrashHeaderFields';
import { useTrashLogForm } from './hooks/useTrashLogForm';
import { formStateToCreateValues, formStateToUpdateValues, trashLogToFormState } from './mappers';
import s from './TrashLog.module.scss';
import TrashLogHistory from './TrashLogHistory/TrashLogHistory';
import type { TrashEntry } from './types';

const DATE_PARAM_RE = /^\d{4}-\d{2}-\d{2}$/;

const isoToParam = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const paramToISO = (param: string | null): string => {
  if (param && DATE_PARAM_RE.test(param)) {
    const d = new Date(`${param}T00:00:00.000Z`);
    if (!Number.isNaN(d.getTime())) return d.toISOString();
  }
  return new Date().toISOString();
};

const newId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const TrashLog = () => {
  const isMobile = useIsMobile();
  const vessel = useVessels((s) => s.vessel);
  const { can } = usePermissions();
  const canSign = can(PERMISSIONS.logbook.TrashLogSign);

  const { view, openHistory, openedFromLogsCard, startNewEntry } = useLogView();
  const [historySearch, setHistorySearch] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const dateParam = searchParams.get('date');
  const isoDate = useMemo(() => paramToISO(dateParam), [dateParam]);
  const dateKey = useMemo(() => isoToParam(isoDate), [isoDate]);

  const { state, logId, setState, setLogId, prefillForm, resetForm } = useTrashLogForm(isoDate);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [addingNew, setAddingNew] = useState(false);

  const { data: fetchedLogs, isFetching } = useTrashLogsByDateQuery(dateKey);

  const fetchedLog = fetchedLogs?.[0];
  const createMutation = useCreateTrashLogMutation();
  const updateMutation = useUpdateTrashLogMutation();
  const signMutation = useSignTrashLogMutation();
  const pdfMutation = useTrashLogPdfMutation();

  useEffect(() => {
    if (fetchedLog) {
      prefillForm(trashLogToFormState(fetchedLog), fetchedLog.id);
    } else {
      resetForm(isoDate);
    }
  }, [fetchedLog, isoDate, prefillForm, resetForm]);

  const navigateToDate = (iso: string) => {
    const param = isoToParam(iso);
    const today = isoToParam(new Date().toISOString());
    setSearchParams(
      (prev) => {
        if (!param || param === today) prev.delete('date');
        else prev.set('date', param);
        // A picked day is always shown as its page, and this has to be one
        // navigation: a second `setSearchParams` in the same tick would start
        // from the old params and drop the date again.
        prev.delete(LOG_VIEW_PARAM);
        return prev;
      },
      { replace: true },
    );
  };

  const handleStartAdd = () => {
    setEditingId(null);
    setAddingNew(true);
  };

  const handleStartEdit = (id: string) => {
    setAddingNew(false);
    setEditingId(id);
  };

  const handleCancel = () => {
    setAddingNew(false);
    setEditingId(null);
  };

  const handleSaveEdit = (id: string, patch: Partial<TrashEntry>) => {
    setState((prev) => ({
      ...prev,
      entries: prev.entries.map((e) => (e.id === id ? { ...e, ...patch } : e)),
    }));
    setEditingId(null);
  };

  const handleAddNew = (values: Omit<TrashEntry, 'id'>) => {
    setState((prev) => ({ ...prev, entries: [...prev.entries, { id: newId(), ...values }] }));
    setAddingNew(false);
  };

  const handleRemove = (id: string) =>
    setState((prev) => ({ ...prev, entries: prev.entries.filter((e) => e.id !== id) }));

  const handleSave = async ({ silent = false } = {}): Promise<string | null> => {
    try {
      if (logId) {
        const saved = await updateMutation.mutateAsync(formStateToUpdateValues(logId, state));
        setLogId(saved.id);
        if (!silent) toast.success('Trash log saved');
        return saved.id;
      }
      const created = await createMutation.mutateAsync(formStateToCreateValues(state));
      setLogId(created.id);
      if (!silent) toast.success('Trash log saved');
      return created.id;
    } catch {
      toast.error('Couldn’t save the trash log. Please try again.');
      return null;
    }
  };

  const handleSign = async () => {
    // Persist any pending edits first so the signed record matches the form.
    const id = await handleSave({ silent: true });
    if (!id) return;
    try {
      await signMutation.mutateAsync({ id });
      toast.success('Trash log signed');
    } catch {
      toast.error('Couldn’t sign the trash log. Please try again.');
    }
  };

  const handleExport = async () => {
    if (!logId) return;
    const blob = await pdfMutation.mutateAsync({ id: logId, timezone: getBrowserTimeZone() });
    await downloadBlob(blob, `trash-log-${dateKey}.pdf`);
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const isSigning = signMutation.isPending || isSaving;
  const isExporting = pdfMutation.isPending;

  const shipName = fetchedLog?.vesselName || vessel?.vessel_name || '';
  const registrationNumber = fetchedLog?.vesselRegistrationNo || vessel?.registration_number || '';
  const onImoNumber = fetchedLog?.vesselImoNumber || vessel?.structure_number_value || '';

  return (
    <>
      <div className={s.toolbar}>
        <div className={s.toolbarControls}>
          {view === 'form' && (
            <ButtonOutlinedSmall onClick={openHistory}>
              <FaClockRotateLeft />
              History
            </ButtonOutlinedSmall>
          )}
          {!isMobile && view === 'form' && (
            <DateNavPicker value={state.date} onChange={navigateToDate} />
          )}
          {view === 'form' && (
            <div className={s.toolbarActions}>
              <ButtonOutlinedSmall
                onClick={() => void handleExport()}
                disabled={isExporting || !logId}
              >
                {isExporting ? 'Exporting…' : 'Export'}
              </ButtonOutlinedSmall>
              <ButtonSmall onClick={() => void handleSave()} disabled={isSaving || isFetching}>
                {isSaving ? 'Saving…' : 'Save'}
              </ButtonSmall>
            </div>
          )}
        </div>
        {view === 'history' && (
          <Input
            placeholder="Search"
            value={historySearch}
            onChange={(e) => setHistorySearch(e.target.value)}
            startIcon={<BiSearch size={18} />}
            className={s.searchInput}
          />
        )}
        {openedFromLogsCard && (
          <ButtonSmall onClick={startNewEntry} aria-label="Add Entry">
            <BiPlus size={20} aria-hidden="true" />
          </ButtonSmall>
        )}
      </div>

      {isMobile && view === 'form' && (
        <DateNavPicker
          value={state.date}
          onChange={navigateToDate}
          className={s.mobileDatePicker}
        />
      )}

      {view === 'history' && (
        <TrashLogHistory search={historySearch} onSelectLog={navigateToDate} />
      )}

      {view === 'form' && (
        <div className={s.content}>
          <div className={s.sectionsList}>
            <Tile>
              <TrashHeaderFields
                shipName={shipName}
                registrationNumber={registrationNumber}
                onImoNumber={onImoNumber}
              />
            </Tile>

            <Tile>
              <TrashCategoriesReference />
            </Tile>

            <TrashEntriesSection
              entries={state.entries}
              editingId={editingId}
              addingNew={addingNew}
              onStartAdd={handleStartAdd}
              onStartEdit={handleStartEdit}
              onCancel={handleCancel}
              onSaveEdit={handleSaveEdit}
              onAddNew={handleAddNew}
              onRemove={handleRemove}
            />

            <Tile>
              <TrashFooterSection
                date={state.date}
                captainName={fetchedLog?.captainName ?? ''}
                captainSignedAt={fetchedLog?.captainSignedAt ?? ''}
                canSign={canSign}
                canSubmitSign={!isFetching}
                isSigning={isSigning}
                onSign={() => void handleSign()}
              />
            </Tile>
          </div>
        </div>
      )}
    </>
  );
};

export default TrashLog;
